/*
 * File: LocaleLink.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 9, 2022 at 9:36 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: July 21, 2025 at 1:22 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { forwardRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Utilities
import { reduceToBaseURL } from '../../../utilities/utilities';
import { parseTitleFromChildren } from '../../utilities/parser';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../../constants/base.constants';

// Styles
import * as S from './LocaleLink.styles';


/**
 * Component
 */

export const LocaleLink = forwardRef(({
	to: toUrl, className, back, identifier, target: initTarget, disableLocale, data, title: titleOverride, ...rest
}, ref) => {

	// Initialize parameters
	let to = toUrl;
	let target = initTarget;

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get location state
	const location = useLocation();

	// Get partner content
	const resourceLink = useMemo(() => partner?.navigation?.links?.resources, [partner]);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Build title
	const title = useMemo(() => (titleOverride == null
		? parseTitleFromChildren(rest.children, t)
		: titleOverride), [titleOverride, rest.children, t]);

	// Get if link is already formatted with analytics
	const hasAnalytics = to != null && to.includes('/_external/link?url=');

	// Update to for partner-specific routes
	if (to === '/resources' && resourceLink?.url) {

		// Update to
		to = resourceLink?.url;

		// Update target if necessary
		if (resourceLink?.new_tab) {
			target = '_blank';
		}
	}

	// Get if link is external
	let isExternal = to != null && (to.startsWith('http://') || to.startsWith('https://') || to.startsWith('www.'));

	// If not core platform, check if dynamic links should be sent to core
	if (!partner.is_core) {

		// Get base url
		const path = reduceToBaseURL(to) || '';

		// Check for dynamic links
		if (['/answer/', '/resource/', '/resources/p/', '/resources/l/'].some((p) => path.startsWith(p))) {

			// Update to url
			to = `https://community.ourwave.org${to}`;

			// Update is external flag
			isExternal = true;
		}
	}

	// Return basic link if necessary
	if (hasAnalytics) {

		// Add path to url
		const url = `${to}&url_path=${location.pathname}`;

		// Return component
		return (
			<S.BaseLink
				aria-label={title}
				title={title}
				{...rest}
				href={url}
				target={target}
				ref={ref}
				rel="noopener"
				className={className ? `${className} animate` : 'animate'}
			/>
		);
	}

	// Render component
	if (isExternal) {

		// Build analytics url
		let url = `${process.env.APP_URL}/_external/link?url=${encodeURIComponent(to)}`;
		if (identifier) url += `&identifier=${identifier}`;

		// Add data parameters to url
		if (data != null) {
			Object.keys(data).forEach((key) => {
				if (data[key] != null) {
					url += `&${encodeURIComponent(key)}=${encodeURIComponent(data[key])}`;
				}
			});
		}

		// Add path to url
		url += `&url_path=${location.pathname}`;

		// Return component
		return (
			<S.BaseLink
				aria-label={title}
				title={title}
				{...rest}
				href={url}
				target={target}
				ref={ref}
				rel="noopener"
				className={className ? `${className} animate` : 'animate'}
			/>
		);

	} if (to != null && (to.startsWith('mailto:') || to.startsWith('tel:') || to.startsWith('sms:'))) {

		// Return component
		return (
			<S.BaseLink
				aria-label={title}
				title={title}
				{...rest}
				href={to}
				target={target}
				ref={ref}
				rel="noopener"
				className={className ? `${className} animate` : 'animate'}
			/>
		);
	}

	// Return component
	return (
		<S.LocaleLink
			aria-label={title}
			title={title}
			{...rest}
			to={!disableLocale ? `${locale.localePath}${to}` : to}
			target={target}
			state={{ back: { path: location.pathname, title: back?.title } }}
			ref={ref}
			className={className ? `${className} animate` : 'animate'}
		/>
	);
});


/**
 * Configuration
 */

LocaleLink.displayName = 'LocaleLink';
LocaleLink.propTypes = {
	to: PropTypes.string,
	className: PropTypes.string,
	target: PropTypes.string,
	identifier: PropTypes.string,
	back: PropTypes.shape(),
	data: PropTypes.shape(),
	disableLocale: PropTypes.bool,
	title: PropTypes.string,
};
LocaleLink.defaultProps = {
	to: null,
	className: null,
	target: null,
	identifier: null,
	back: {},
	data: {},
	disableLocale: false,
	title: null,
};
